import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchAnalysis, createAnalysis, deleteAnalysis, fetchAnalyzeDocuments, updateAnalyzeDocuments } from '../../../store/startup/analysis/analysisSlice';
import {
    Box,
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Modal,
    Paper,
    Grid2,
    IconButton
} from '@mui/material';
import 'react-quill/dist/quill.snow.css';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import SingleFileUpload from "../../fileUpload/FileUpload";
import { Add } from '@mui/icons-material';
import { toast } from 'react-toastify';

const StartupAnalysis = () => {
    const dispatch = useDispatch();
    const { startupId } = useParams();
    const { analysisByType, analyzeDocuments } = useSelector(state => state.analysis);
    const [open, setOpen] = useState(false);
    const [selectedType, setSelectedType] = useState(1);
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');
    
    // Dosya yolları için state'ler
    const [swotDocumentPath, setSwotDocumentPath] = useState('');
    const [businessPlanDocumentPath, setBusinessPlanDocumentPath] = useState('');
    const [investorPresentationPath, setInvestorPresentationPath] = useState('');

    useEffect(() => {
        if (startupId) {
            dispatch(fetchAnalysis(startupId));
            dispatch(fetchAnalyzeDocuments(startupId));
        }
    }, [dispatch, startupId]);

    useEffect(() => {
        if (analyzeDocuments) {
            setSwotDocumentPath(analyzeDocuments.swotAnalysisPath || null);
            setBusinessPlanDocumentPath(analyzeDocuments.businessPlanPath || null);
            setInvestorPresentationPath(analyzeDocuments.investorPresentationPath || null);
        }
    }, [analyzeDocuments]);

    const handleOpen = (type) => {
        setSelectedType(type);
        setDescription('');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setDescription('');
    };

    const handleSave = async () => {
        setError('');

        if (!description.trim()) {
            setError('Açıklama alanı boş bırakılamaz');
            return;
        }

        try {
            await dispatch(createAnalysis({
                startupId,
                analyzeType: selectedType,
                description
            })).unwrap();
            await dispatch(fetchAnalysis(startupId));
            handleClose();
        } catch (error) {
            console.error('Failed to create analysis:', error);
        }
    };

    const handleDelete = async (analysisId) => {
        try {
            await dispatch(deleteAnalysis(analysisId)).unwrap();
            await dispatch(fetchAnalysis(startupId));
        } catch (error) {
            console.error('Failed to delete analysis:', error);
        }
    };
    const handleSwotUpload = async (filePath) => {
        try {
            await dispatch(updateAnalyzeDocuments({
                startupId,
                swotAnalysisPath: filePath
            })).unwrap();
            setSwotDocumentPath(filePath);
            toast.success('SWOT analizi belgesi güncellendi');
        } catch (error) {
            console.error('Failed to update SWOT document:', error);
            toast.error('SWOT analizi belgesi güncellenirken hata oluştu');
        }
    };

    const handleBusinessPlanUpload = async (filePath) => {
        try {
            await dispatch(updateAnalyzeDocuments({
                startupId,
                businessPlanPath: filePath
            })).unwrap();
            setBusinessPlanDocumentPath(filePath);
            toast.success('İş planı belgesi güncellendi');
        } catch (error) {
            console.error('Failed to update business plan:', error);
            toast.error('İş planı belgesi güncellenirken hata oluştu');
        }
    };

    const handleInvestorPresentationUpload = async (filePath) => {
        try {
            await dispatch(updateAnalyzeDocuments({
                startupId,
                investorPresentationPath: filePath
            })).unwrap();
            setInvestorPresentationPath(filePath);
            toast.success('Yatırımcı sunumu güncellendi');
        } catch (error) {
            console.error('Failed to update investor presentation:', error);
            toast.error('Yatırımcı sunumu güncellenirken hata oluştu');
        }
    };

    const analysisTypes = {
        1: "Ürünün/Projenin Güçlü Yönleri Nelerdir?",
        2: "Ürünün/Projenin Zayıf Yönleri Nelerdir?",
        3: "Ürünün/Projenin Ortaya Çıkardığı Fırsatlar Nelerdir?",
        4: "Ürünün/Projenin Gerçekleşmesinde Olası Tehditler Nelerdir?",
        5: "Zayıf Yönleri Nasıl Güçlendireceksin?",
        6: "Tehditleri Nasıl Ortadan Kaldıracaksın?"
    };

    return (
        <div>
            {Object.keys(analysisTypes).map((type) => (
                <div key={type}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {analysisTypes[type]}
                    </Typography>
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Açıklama</TableCell>
                                    <TableCell align="right">İşlemler</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {analysisByType[type]?.map((analysis) => (
                                    <TableRow key={analysis.startupAnalyzeId}>
                                        <TableCell>{analysis.description}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(analysis.startupAnalyzeId)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell colSpan={2} align="center">
                                        <Button
                                            startIcon={<Add />}
                                            onClick={() => handleOpen(parseInt(type))}
                                            color="primary"
                                        >
                                            Yeni Ekle
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            ))}

            <Box sx={{ display: 'flex', width: '100%' }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                    Dokümanlar
                </Typography>
                <Divider sx={{ marginBottom: 3 }} />
            </Box>

            <Box>
                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                        <Typography sx={{ mb: 0 }}>
                            Yatırımcı Sunumu
                        </Typography>
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }} >
                            <SingleFileUpload 
                                onUploadComplete={handleInvestorPresentationUpload}
                                existFilePath={investorPresentationPath}
                                fileUploadPath="startup"
                            />
                        </Box>
                    </Grid2>
                </Grid2>
            </Box>

            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-title"
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <Box sx={{ 
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    width: 800,
                    borderRadius: 1
                }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {analysisTypes[selectedType]}
                    </Typography>
                    <TextField 
                        label="Açıklama" 
                        fullWidth 
                        multiline 
                        rows={4} 
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        error={!!error}
                        helperText={error}
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button variant="outlined" color="error" onClick={handleClose}>
                            İptal
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleSave}>
                            Kaydet
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
};

export default StartupAnalysis;